import { Component, EventEmitter, Output, ChangeDetectorRef, OnDestroy, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { EfileAdminService } from '../efile-admin.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-bulk-xml-sync',
    templateUrl: './bulk-xml-sync.component.html',
    styleUrls: ['./bulk-xml-sync.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BulkXmlSyncComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  JCD_KEY = 250;
  gService: any = {};

  showModal = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Sync XML to SLT',
    message: 'Are you sure you want to sync all the xmls shown to SLT?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private efileAdminService: EfileAdminService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => this.applyWorkspaceContext())
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => this.applyWorkspaceContext())
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.gService = this.gtwGlobal.global || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.cdr.markForCheck();
  }

  onCancel(): void {
    this.showModal = false;
    this.requestGridUpdate.emit('cancel');
  }

  onConfirm(): void {
    const taxYear = this.filterParams?.tax_year ?? this.globalService?.tax_year ?? '';
    const selectedTaxYear = _.find((this.gService?.inputs?.tax_year?.values || []), { label: taxYear });
    const scenario = selectedTaxYear?.scenarios?.find((s: any) =>
      s.jcd_key == this.JCD_KEY && s.default_scenario === 'Y'
    )?.scenario ?? '';

    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    const bulkPdfEngineUrl =
      this.GENERAL_CONFIG?.bulk_pdf_engine_api_url ??
      this.GENERAL_CONFIG?.bulkPdfEngine ??
      '';

    this.efileAdminService.bulkSyncXML(bulkPdfEngineUrl, taxYear, scenario, this.JCD_KEY).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.detectChanges();
        const data = res?.data ?? res;
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Bulk XML Sync was Completed!', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.errorMessage || 'Error', 'gtw-snackbar--red');
        }
      },
      error: () => {
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.cdr.detectChanges();
        this.gtwSnackbarService.openSnackBar('Error occurred.', 'gtw-snackbar--red');
      }
    });
  }
}
